import type { HrMetricId, MetricDirection } from '../services/hrImportData';

export type HrKpiReferenceLink = { labelTh: string; url: string };

export type HrKpiDefinition = {
  metricId: HrMetricId;
  titleTh: string;
  direction: MetricDirection;
  definitionTh: string;
  calculationTh?: string;
  benchmarkTextTh?: string;
  code?: string;
  categoryTh?: string;
  frequencyTh?: string;
  unitTh?: string;
  numeratorTh?: string;
  denominatorTh?: string;
  exclusionsTh?: string[];
  objectiveTh?: string;
  responsibleUnitTh?: string;
  effectiveDateTh?: string;
  referenceLinks: HrKpiReferenceLink[];
  updatedAtTh?: string;
};

/**
 * Code-defined KPI definitions (same idea as FallIncidentDashboard constants).
 * Keep URLs empty until you have share links; UI should handle empty lists gracefully.
 */
export const HR_KPI_DEFINITIONS: Record<HrMetricId, HrKpiDefinition> = {
  turnoverRate: {
    metricId: 'turnoverRate',
    titleTh: 'ร้อยละการลาออกของพยาบาล',
    code: 'DNS03',
    categoryTh: 'ฝ่ายการพยาบาล',
    direction: 'lowerIsBetter',
    unitTh: 'ร้อยละ',
    frequencyTh: 'รายไตรมาส (ทุก 3 เดือน)',
    objectiveTh: 'ประเมินประสิทธิผลขององค์กร',
    definitionTh:
      'ติดตามการลาออกโดยสมัครใจของพยาบาล โดยไม่นับการโอนย้ายไปหน่วยงานภายนอก การถูกเลิกจ้าง การสิ้นสุดการจ้าง/ยุติสัญญา การเกษียณ (รวมเกษียณก่อนกำหนด) และการเสียชีวิต',
    calculationTh: '(a / b) × 100',
    numeratorTh: 'จำนวนพยาบาลที่ลาออกในไตรมาสนั้น',
    denominatorTh: 'จำนวนพยาบาล ณ วันสุดท้ายของไตรมาสนั้น',
    exclusionsTh: [
      'โอนย้ายไปหน่วยงานภายนอก (เช่น โรงพยาบาลจุฬาลงกรณ์, ศูนย์บริการโลหิตแห่งชาติ สภากาชาดไทย)',
      'ถูกเลิกจ้าง',
      'สิ้นสุดการจ้าง/ยุติสัญญา',
      'เกษียณอายุ (รวมเกษียณก่อนกำหนด)',
      'เสียชีวิต',
    ],
    benchmarkTextTh: 'เป้าหมาย: ≤ 5',
    responsibleUnitTh: 'คณะกรรมการบริหารทรัพยากรบุคคล ฝ่ายการพยาบาล',
    effectiveDateTh: '1 ตุลาคม 2020',
    referenceLinks: [
      {
        labelTh: 'ตัวชี้วัดที่ 3 ร้อยละการลาออกของพยาบาล',
        url: 'https://drive.google.com/file/d/1jPdlC4b_HgUhtVGMn6Q5zEhj9o_qMwp0/view?usp=drive_link',
      },
    ],
  },

  jobSatisfaction: {
    metricId: 'jobSatisfaction',
    titleTh: 'ร้อยละความพึงพอใจของบุคลากรฝ่ายการพยาบาล (เฉลี่ย)',
    code: 'DNS01',
    direction: 'higherIsBetter',
    unitTh: 'ร้อยละ',
    frequencyTh: 'รายปี',
    objectiveTh: 'ประเมินระดับความพึงพอใจของบุคลากรที่ปฏิบัติงานภายใต้ฝ่ายการพยาบาล',
    definitionTh:
      'วัดระดับความพึงพอใจในงานของบุคลากรฝ่ายการพยาบาลจากแบบสอบถามมาตรฐาน (ระดับความพึงพอใจ 5 ระดับ ตั้งแต่ต่ำสุดถึงสูงสุด)',
    calculationTh: '(a / b) × 100',
    numeratorTh: 'ผลรวมคะแนนความพึงพอใจของผู้ตอบแบบสอบถาม',
    denominatorTh: 'คะแนนความพึงพอใจสูงสุดที่เป็นไปได้ของผู้ตอบแบบสอบถามทั้งหมด',
    benchmarkTextTh: 'เป้าหมาย: ≥ 80%',
    responsibleUnitTh: 'คณะกรรมการทรัพยากรบุคคล ฝ่ายการพยาบาล',
    effectiveDateTh: 'ใช้ตั้งแต่ปีงบประมาณ 2014',
    referenceLinks: [
      {
        labelTh: 'ตัวชี้วัดที่ 1 ร้อยละคะแนนเฉลี่ยความพึงพอใจของบุคลากร',
        url: 'https://drive.google.com/file/d/132ugxXlr_CRT5iWhHfFKnH4_aKtEEeE4/view',
      },
    ],
  },

  engagement: {
    metricId: 'engagement',
    titleTh: 'ร้อยละคะแนนเฉลี่ยความผูกพันของบุคลากร',
    code: 'DNS02',
    direction: 'higherIsBetter',
    unitTh: 'ร้อยละ',
    frequencyTh: 'รายปี',
    objectiveTh:
      'ประเมินประสิทธิผลของการบริหารจัดการและสภาพแวดล้อมการทำงานต่อบุคลากรปฏิบัติงาน',
    definitionTh:
      'ความผูกพันของบุคลากร คือความสัมพันธ์ระหว่างบุคคลกับองค์กร สะท้อนความรู้สึกเป็นส่วนหนึ่งขององค์กร ความศรัทธา ความจงรักภักดี ความตั้งใจทุ่มเท และความต้องการคงอยู่เป็นสมาชิกขององค์กรเพื่อให้บรรลุเป้าหมาย',
    benchmarkTextTh: 'เป้าหมาย: ≥ 75%',
    responsibleUnitTh: 'งานบริหารทรัพยากรบุคคล (เก็บข้อมูล) • ฝ่ายการพยาบาล (ติดตาม/รายงาน)',
    referenceLinks: [
      {
        labelTh: 'ตัวชี้วัดที่ 2 ร้อยละคะแนนเฉลี่ยความผูกพันของบุคลากร',
        url: 'https://drive.google.com/file/d/1bvUbLc1satnLN52xa559mzZ-sUkBXvjW/view',
      },
    ],
  },

  happinometer: {
    metricId: 'happinometer',
    titleTh: 'ดัชนีความสุขของบุคลากรฝ่ายการพยาบาล (เฉลี่ย)',
    direction: 'higherIsBetter',
    unitTh: 'ร้อยละ',
    frequencyTh: 'รายปี',
    objectiveTh: 'ประเมินระดับความสุขของบุคลากรที่ปฏิบัติงานภายใต้ฝ่ายการพยาบาล',
    definitionTh:
      'คะแนนเฉลี่ยจากแบบประเมินความสุขของบุคลากร ตามนิยามและแบบสอบถามของหน่วยงานที่กำหนด',
    benchmarkTextTh: 'เป้าหมาย: (รอระบุเกณฑ์อ้างอิง)',
    referenceLinks: [{ labelTh: 'เอกสารนิยามดัชนีความสุข', url: '' }],
  },
};

